import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { DomSanitizer } from '@angular/platform-browser';
import * as _ from 'lodash';
import { Observable, Subject, catchError, throwError } from 'rxjs';

import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { CuttingFacadeOutline, PresetSettings } from 'cloud-shared-lib';
import { CuttingPanelHelper } from '../../../helpers/cutting-panel.helper';
import { CuttingButt } from '../../../models/cutting/cutting-butt.model';
import { CuttingCustomHole } from '../../../models/cutting/cutting-custom-hole.model';
import { CuttingFixedFastenersSet } from '../../../models/cutting/cutting-fixed-fasteners-set.model';
import { CuttingMaterial, MaterialType } from '../../../models/cutting/cutting-material.model';
import { CuttingPanelBaseSlot } from '../../../models/cutting/cutting-panel-base-slot.model';
import {
  AngleMode,
  Cutout,
  CuttingPanelOutline,
  FaceBand,
  OutlineAngle,
} from '../../../models/cutting/cutting-panel-outline.model';
import { CuttingPanelSlot } from '../../../models/cutting/cutting-panel-slot.model';
import { CuttingPanel } from '../../../models/cutting/cutting-panel.model';
import { CuttingSettings, PanelSettings, PostformingSideEnum } from '../../../models/user/cutting-settings.model';
import { RestrictionMaxMin } from '../../../models/user/outline-editor-options.model';
import { CuttingPanelListService } from '../cutting-panel-list.service';
import { PanelListDataService } from '../panel-list-data.service';
import { RotationDirectionEnum, rotatePanel } from './panel-rotation';

export interface Butts {
  top: CuttingButt;
  bottom: CuttingButt;
  left: CuttingButt;
  right: CuttingButt;
}

export interface Grooves {
  top: CuttingPanelSlot;
  bottom: CuttingPanelSlot;
  left: CuttingPanelSlot;
  right: CuttingPanelSlot;
}

export interface FastenersBySides {
  top: { holes: CuttingCustomHole[]; sets: CuttingFixedFastenersSet[] };
  bottom: { holes: CuttingCustomHole[]; sets: CuttingFixedFastenersSet[] };
  left: { holes: CuttingCustomHole[]; sets: CuttingFixedFastenersSet[] };
  right: { holes: CuttingCustomHole[]; sets: CuttingFixedFastenersSet[] };
}

export enum OutlineEditorTabsEnum {
  Butts,
  Angles,
  Offcuts,
  Slots,
  Fasteners,
  FaceBanding,
}

@Component({
  templateUrl: './panel-outline-editor-dialog.component.html',
  styleUrls: ['./panel-outline-editor-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PanelOutlineEditorDialogComponent implements OnInit {
  userOptions: CuttingSettings;
  panel: CuttingPanel = new CuttingPanel();
  activeTab: OutlineEditorTabsEnum = OutlineEditorTabsEnum.Butts;
  buttsValid = true;
  hasVeryNarrowButts = { value: false, minButtWidth: 0 };
  anglesValid = true;

  postforming: PostformingSideEnum = PostformingSideEnum.None;

  materialGroupsWithPostforming: Map<number, PostformingSideEnum> = new Map();

  downloadAttachment$: Observable<Blob>;

  get paramsValid(): boolean {
    return this.buttsValid && this.anglesValid;
  }

  @Input() adminMode = false;
  @Input() orderId: number;
  @Input() editable = true;
  @Input() matInMatBaseId = 0;
  @Input() mainMaterial: CuttingMaterial;
  @Input() set panelMaterial(val: CuttingPanel) {
    this.panel = _.cloneDeep(val);
  }
  @Input() presetSettings: PresetSettings;

  @Output() createMirrorPanel: EventEmitter<CuttingPanel> = new EventEmitter();
  @Output() clonePanelEmit: Subject<CuttingPanel> = new Subject();

  constructor(
    public dialogRef: MatDialogRef<PanelOutlineEditorDialogComponent>,
    private cuttingPanelListService: CuttingPanelListService,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private snackBar: MatSnackBar,
    private panelListDataService: PanelListDataService
  ) {
    this.iconRegistry.addSvgIcon(
      'mirror_flip',
      this.sanitizer.bypassSecurityTrustResourceUrl('assets/images/addition_custom_icons/mirror_flip.svg')
    );
  }

  ngOnInit(): void {
    this.cuttingPanelListService.onCuttingOptionsChanged.subscribe(response => {
      this.userOptions = response;
      this.materialGroupsWithPostforming = new Map(
        this.userOptions.postforming
          .filter(m => m.editableWidth || m.postformingSide !== PostformingSideEnum.None)
          .map(m => [m.inMatBaseId, m.postformingSide])
      );
      this.activeTab = this.findIndexActiveTab(this.activeTab);
      this.postforming =
        this.materialGroupsWithPostforming.get(this.mainMaterial.inMaterialBaseGroupId) || PostformingSideEnum.None;
    });
  }

  onCutoutsFormChanged(items: Cutout[]): void {
    const outline = this.panel.panelOutline;

    outline.cutouts = items;
  }

  onAnglesFormChanged(angles: CuttingPanelOutline<CuttingPanelBaseSlot> | CuttingFacadeOutline): void {
    this.panel.panelOutline.topLeft = angles.topLeft;
    this.panel.panelOutline.topRight = angles.topRight;
    this.panel.panelOutline.bottomLeft = angles.bottomLeft;
    this.panel.panelOutline.bottomRight = angles.bottomRight;

    this.anglesValid = CuttingPanelHelper.checkAnglesPanelOutlineValid(
      this.panel,
      this.userOptions.outlineEditorOptions
    );

    const removeButtForInsideCutting = (angle: OutlineAngle): void => {
      if (angle.mode === AngleMode.CuttedInside) {
        angle.butt = undefined;
      }
    };

    const removeButtForInsideCuttingWithAngle = (angle: OutlineAngle): void => {
      if (angle.mode === AngleMode.CuttedInsideWithAngle) {
        angle.butt = undefined;
      }
    };

    if (!this.userOptions.outlineEditorOptions.angles.insideCutting.restrictions.addButtToCutting) {
      removeButtForInsideCutting(angles.topLeft);
      removeButtForInsideCutting(angles.topRight);
      removeButtForInsideCutting(angles.bottomLeft);
      removeButtForInsideCutting(angles.bottomRight);
    }

    if (!this.userOptions.outlineEditorOptions.angles.insideCuttingWithAngle.restrictions.addButtToCutting) {
      removeButtForInsideCuttingWithAngle(angles.topLeft);
      removeButtForInsideCuttingWithAngle(angles.topRight);
      removeButtForInsideCuttingWithAngle(angles.bottomLeft);
      removeButtForInsideCuttingWithAngle(angles.bottomRight);
    }

    if (this.userOptions.outlineEditorOptions.angles.rounding.restrictions.addButtsToAdjacentSides) {
      if (angles.topLeft.mode === AngleMode.Rounded && angles.topLeft.butt !== undefined) {
        if (this.postforming !== PostformingSideEnum.TopAndBottom) {
          this.panel.l1 = angles.topLeft.butt;
        }
        this.panel.w1 = angles.topLeft.butt;
      }

      if (angles.topRight.mode === AngleMode.Rounded && angles.topRight.butt !== undefined) {
        if (this.postforming !== PostformingSideEnum.TopAndBottom) {
          this.panel.l1 = angles.topRight.butt;
        }
        this.panel.w2 = angles.topRight.butt;
      }

      if (angles.bottomLeft.mode === AngleMode.Rounded && angles.bottomLeft.butt !== undefined) {
        if (this.postforming !== PostformingSideEnum.Bottom) {
          this.panel.l2 = angles.bottomLeft.butt;
        }
        this.panel.w1 = angles.bottomLeft.butt;
      }

      if (angles.bottomRight.mode === AngleMode.Rounded && angles.bottomRight.butt !== undefined) {
        if (this.postforming !== PostformingSideEnum.Bottom) {
          this.panel.l2 = angles.bottomRight.butt;
        }
        this.panel.w2 = angles.bottomRight.butt;
      }
    }

    // TODO: move angle butt to butt tab and delete this line
    this.buttsValid = CuttingPanelHelper.checkButtsPanelOutlineValid(this.panel, this.postforming);
  }

  onButtFormChanged(butts: Butts): void {
    if (butts) {
      this.panel.l1 = butts.top;
      this.panel.l2 = butts.bottom;
      this.panel.w1 = butts.left;
      this.panel.w2 = butts.right;

      this.buttsValid = CuttingPanelHelper.checkButtsPanelOutlineValid(this.panel, this.postforming);

      this.checkButtWidth();
    }
  }

  onGrooveFormChanged(grooves: Grooves): void {
    if (grooves) {
      this.panel.panelOutline.topSide.groove = grooves.top;
      this.panel.panelOutline.bottomSide.groove = grooves.bottom;
      this.panel.panelOutline.leftSide.groove = grooves.left;
      this.panel.panelOutline.rightSide.groove = grooves.right;
    }
  }

  onCustomHolesFormChanged(fasteners: FastenersBySides): void {
    this.panel.panelOutline.topSide.customHoles = fasteners.top.holes;
    this.panel.panelOutline.bottomSide.customHoles = fasteners.bottom.holes;
    this.panel.panelOutline.rightSide.customHoles = fasteners.right.holes;
    this.panel.panelOutline.leftSide.customHoles = fasteners.left.holes;

    this.panel.panelOutline.topSide.fastenersSets = fasteners.top.sets;
    this.panel.panelOutline.bottomSide.fastenersSets = fasteners.bottom.sets;
    this.panel.panelOutline.rightSide.fastenersSets = fasteners.right.sets;
    this.panel.panelOutline.leftSide.fastenersSets = fasteners.left.sets;
  }

  onFaceBandingFormChanged(bands: FaceBand[]): void {
    this.panel.panelOutline.faceBanding = bands;

    this.checkButtWidth();
  }

  private checkButtWidth(): void {
    let totalThickness =
      this.panel.panelOutline.faceBanding.map(b => b.material.thickness).reduce((a, b) => a + b, 0) +
      this.mainMaterial.thickness;

    if (this.userOptions.butts.checkButtWidth) {
      totalThickness = totalThickness + this.userOptions.butts.minDifferenceBetweenButtWidthAndPanelThickness;
    }

    const narrowestButt = Math.min(
      this.panel.l1 ? this.panel.l1.width : totalThickness,
      this.panel.l2 ? this.panel.l2.width : totalThickness,
      this.panel.w1 ? this.panel.w1.width : totalThickness,
      this.panel.w2 ? this.panel.w2.width : totalThickness
    );

    if (narrowestButt < totalThickness) {
      this.hasVeryNarrowButts = { value: true, minButtWidth: totalThickness };
    } else {
      this.hasVeryNarrowButts = { value: false, minButtWidth: 0 };
    }
  }

  addAttachment(file: File): void {
    this.cuttingPanelListService.onPanelFileAttached.next(file);
    this.panel.attachmentName = file.name;
  }

  removeAttachment(): void {
    this.panel.attachmentName = '';
  }

  hasFasteners(): boolean {
    return CuttingPanelHelper.hasFasteners(this.panel);
  }

  hasCutouts(): boolean {
    return CuttingPanelHelper.hasCutouts(this.panel);
  }

  onDownloadAttachment(): void {
    this.downloadAttachment$ = this.panelListDataService
      .downloadPanelAttachment(this.orderId, this.matInMatBaseId, this.panel.attachmentName)
      .pipe(
        catchError(err => {
          this.snackBar.open($localize`Не удалось скачать прикрепленный файл`);
          return throwError(() => err);
        })
      );
  }

  saveAndClose(): void {
    const { valid: validSizes, error: sizeError } = this.checkPanelSize(this.panel, this.userOptions.panels);
    if (!validSizes) {
      const message = $localize`Минимальный габаритный размер: ${sizeError?.heightRange?.min}×${sizeError?.widthRange?.min}.
       Максимальный габаритный размер: ${sizeError?.heightRange?.max}×${sizeError?.widthRange?.max}`;

      this.snackBar.open(message);
      return;
    }

    if (this.hasVeryNarrowButts.value) {
      const message = $localize`Ширина облицовки не должна быть меньше ${this.hasVeryNarrowButts.minButtWidth.toString()}`;

      this.snackBar.open(message, 'OK', { duration: 0 });
      return;
    }

    if (!this.buttsValid) {
      this.snackBar.open(
        $localize`Облицовка на углу с обработкой "Наружный радиус" и на двух сопряженных
      сторонах должна быть одинаковой`,
        'OK',
        { duration: 0 }
      );
      return;
    }

    if (!this.anglesValid) {
      this.snackBar.open($localize`Некорректно заданы параметры обработки угла`, 'OK', { duration: 0 });
      return;
    }

    if (!this.paramsValid) {
      this.snackBar.open($localize`Необходимо исправить ошибки`, 'OK', { duration: 0 });
      return;
    }

    this.dialogRef.close({ panel: this.panel, attachmentName: this.panel.attachmentName });
  }

  clonePanel(panel: CuttingPanel): void {
    this.clonePanelEmit.next(panel);
    this.saveAndClose();
  }

  rotatePanelClockwise(panel: CuttingPanel): void {
    rotatePanel(panel, RotationDirectionEnum.Clockwise);
  }

  rotatePanelCounterclockwise(panel: CuttingPanel): void {
    rotatePanel(panel, RotationDirectionEnum.Counterclockwise);
  }

  get isLinearMaterial(): boolean {
    return this.mainMaterial.type === MaterialType.Linear;
  }

  private findIndexActiveTab(activeTab: OutlineEditorTabsEnum): number {
    const tabs: number[] = [];
    if (this.presetSettings.componentsAvailability.outlineEditor.butts) tabs.push(OutlineEditorTabsEnum.Butts);
    if (this.presetSettings.componentsAvailability.outlineEditor.angles) tabs.push(OutlineEditorTabsEnum.Angles);
    if (this.presetSettings.componentsAvailability.outlineEditor.cutouts) tabs.push(OutlineEditorTabsEnum.Offcuts);
    if (this.presetSettings.componentsAvailability.outlineEditor.slots) tabs.push(OutlineEditorTabsEnum.Slots);
    if (this.presetSettings.componentsAvailability.outlineEditor.fasteners) tabs.push(OutlineEditorTabsEnum.Fasteners);
    if (this.presetSettings.componentsAvailability.outlineEditor.faceBanding)
      tabs.push(OutlineEditorTabsEnum.FaceBanding);
    activeTab = tabs.indexOf(activeTab);
    return activeTab;
  }

  private checkPanelSize(
    panel: CuttingPanel,
    settings: PanelSettings
  ): { valid: boolean; error: { heightRange: RestrictionMaxMin; widthRange: RestrictionMaxMin } | null } {
    const { valid, widthRange, heightRange } =
      !settings || !panel
        ? { valid: true, widthRange: null, heightRange: null }
        : CuttingPanelHelper.checkPanelSize(panel, settings, this.mainMaterial.height, this.mainMaterial.width);

    if (!valid) {
      const error = { heightRange, widthRange };

      return { valid, error };
    }

    return { valid, error: null };
  }
}
